// Pure kanban parsing, sanitization, and prompt-rendering helpers.
//
// This is the leaf half of the manager's kanban subsystem: template-id
// classification, LLM-output parsing (standup / plan / kanban-update), string
// sanitization, board-item predicates, and board-for-prompt rendering. Every
// function here is pure — none touches the filesystem, the API client, tmux,
// the persistent session, or any other manager module — so the whole module is
// importable by the scheduler,
// the inject/harvest path, and `manager-worker.ts` itself without creating an
// import cycle. The stateful kanban code (maybeInjectKanbanCheck, harvest,
// cron-monitor) stays in manager-worker.ts and imports the pieces it needs from
// here. See ADR-0011.

import { KANBAN_NUDGE_ACTIONABLE_STATUSES } from '@augmented/core';

// Shape of a kanban board item as returned by /host/my-kanban. Shared between
// the parsers/predicates here and the stateful kanban code in manager-worker.ts.
// ENG-5769: source_integration / source_external_id carry the originating
// channel + thread coordinates captured by `kanban_add` (ENG-5382). They flow
// through the kanban-work nudge so the agent resumes a card knowing which
// Slack/Telegram thread to reply in — previously only CLAUDE.md's Active
// Tasks section had them, and that section was gated behind a default-off
// env flag.
export type BoardItem = { id: string; title: string; status: string; priority: number; estimated_minutes?: number; deliverable?: string; result?: string; notify_channel?: string; notify_to?: string; updated_at?: string; source_type?: string; source_ref?: string; source_integration?: string | null; source_external_id?: string | null; source_url?: string | null;
  // ENG-7643 (ADR-0044): the waiting-status shape. Returned by /host/my-kanban's
  // `select('*')` already; declared here so the manager's waiting-human requester
  // notify (P5a) can read them. waiting_context.choices are the agent-offered
  // response options; metadata.requester = who to DM when the card parks.
  waiting_kind?: string | null;
  waiting_on?: string | null;
  waiting_context?: { choices?: Array<{ label: string; value: string }>; details?: string } | null;
  metadata?: { requester?: { channel: string; sender_id: string; external_id: string | null } } | null;
  // ENG-7958: polymorphic assignee (migration 20260618000004). 'agent' (default)
  // or 'human'. Returned by /host/my-kanban's `select('*')`; declared so
  // isHybridActionable can exclude human-assigned cards from the kanban nudge.
  assignee_type?: string | null;
  // CS-1651: the agent's declaration that this card's result must NOT be
  // messaged to anyone. Set by `kanban_done(suppress_delivery: true)`.
  //
  // Declared here because there are TWO independent delivery paths for a done
  // card and only one of them honoured the flag. The scheduled-card path
  // (manager/scheduler/kanban-route.ts) checked it; the manager's newly-done
  // board-diff notifier did not, and could not - `BoardItem` did not carry the
  // field, so the value was not merely unread, it never arrived. Reported by
  // horatio after a customer received a wall of internal detail every 30
  // minutes for a working day while the tool reported the delivery suppressed.
  suppress_delivery?: boolean | null };

// Template IDs that map to agent field updates
export const STANDUP_TEMPLATES = new Set(['daily-standup', 'end-of-day-summary']);
export const TASK_UPDATE_TEMPLATES = new Set(['hourly-status', 'task-update']);
export const PLAN_TEMPLATES = new Set(['morning-plan']);
export const KANBAN_WORK_TEMPLATES = new Set(['kanban-work']);
// ENG-7385: templates that deliver their result by CALLING A TOOL, not by having
// the manager message the card result. The weekly performance review runs the
// `submit_performance_review` tool (which DMs the agent's manager the rating
// form); its card is only a working record. These must NOT get the generic
// "write-to-card / don't message anyone" delivery contract, which contradicts the
// tool call and made agents file a self-review card while the manager got nothing.
// (Value mirrors PERFORMANCE_REVIEW_TEMPLATE_ID in @augmented/core.)
export const TOOL_DELIVERED_TEMPLATES = new Set(['performance-review']);

// ENG-5599 (S1 of ENG-5576): a "plain" scheduled task is one whose claude -p
// result only needs delivering — no template-specific side effects in
// processClaudeTaskResult (standup posts to /host/agent-status; plan /
// task-update write board items) and not the kanban-work loop. Only plain
// tasks are eligible for the in-session kanban route in S1; the rest keep the
// oneshot until their side effects are re-homed in-session (S3, ENG-5601).
export function isPlainScheduledTemplate(templateId: string): boolean {
  return (
    !STANDUP_TEMPLATES.has(templateId) &&
    !TASK_UPDATE_TEMPLATES.has(templateId) &&
    !PLAN_TEMPLATES.has(templateId) &&
    !KANBAN_WORK_TEMPLATES.has(templateId)
  );
}

// ENG-6546 (S6 of ENG-5576): templates whose side effect is applied MANAGER-side
// by parsing the done card's result and POSTing it — the standup + task-update
// families, both of which write to `POST /host/agent-status` (an idempotent
// UPSERT of the agent's standup / current_tasks fields). These are now eligible
// to route in-session: the side effect runs in deliverScheduledCardResult on the
// agent's self-completion, where before they were forced onto the `claude -p`
// oneshot. Because the post is an idempotent overwrite, a delivery retry re-posts
// the same status harmlessly — no double-apply. morning-plan is deliberately
// EXCLUDED: its side effect (creating board cards) is a non-idempotent INSERT, so
// it is re-homed by having the in-session agent call kanban_add itself rather
// than the manager parsing (ENG-6546 step 2). kanban-work never routes here.
export function isManagerSideEffectTemplate(templateId: string): boolean {
  return STANDUP_TEMPLATES.has(templateId) || TASK_UPDATE_TEMPLATES.has(templateId);
}

// ENG-5435 + ENG-5662: manager-gated hybrid kanban-work injection is now the
// SOLE kanban-work mechanism. The two predecessors it replaced — the external
// `claude -p kanban-work` cron (ENG-5405) and the in-session `/loop kanban-work`
// (ENG-5404) — have been removed, along with their AGT_DISABLE_KANBAN_WORK_CRON /
// AGT_DISABLE_KANBAN_LOOP kill switches and the AGT_KANBAN_HYBRID opt-in flag.
// Kept as a function (rather than inlining `true`) so the step-7e poll block and
// its call sites read unchanged. The trigger lives in step 7e of the per-agent
// poll; helpers below.
export function isKanbanHybridEnabled(): boolean {
  return true;
}

export function isKanbanHybridDryRun(): boolean {
  const v = process.env['AGT_KANBAN_HYBRID_DRY_RUN'];
  return v === '1' || v?.toLowerCase() === 'true';
}

// Same status set as ACTIONABLE_STATUSES (both exclude `backlog` as of
// ENG-5605 — backlog is curatorial, not "pick this up now"; the board owner
// promotes backlog → todo when ready). The hybrid path's *additional* narrowing
// over the plain kanban-work gate is the scheduled_task exclusion in
// isHybridActionable below, not the status set.
// ENG-7557: derived from the shared core constant so the API's consume-time
// revalidation (stale-nudge expiry) can never drift from the manager's
// enqueue-time gate.
export const HYBRID_ACTIONABLE_STATUSES = new Set<string>(KANBAN_NUDGE_ACTIONABLE_STATUSES);

// ENG-5600: a board item the generic hybrid nudge should act on. Excludes
// scheduled_task cards — those are driven by routeScheduledTaskViaKanban's own
// per-card nudge + run (ENG-5599). Letting the board-level nudge also fire for
// them opens a competing source_type='kanban' run whose marker would absorb the
// card's turns, mis-attributing the scheduled task's cost (and double-nudging).
//
// ENG-7958: also excludes HUMAN-assigned cards. The polymorphic-assignee epic
// (migration 20260618000004) parks human tasks on the same board; a card in
// todo/in_progress with assignee_type='human' is a PERSON's work, not the
// agent's, so nudging the agent to "pick up actionable items" for it fired
// kanban_list forever at an agent with an otherwise-empty board (Maven). Only
// an explicit 'human' is excluded: assignee_type is NOT NULL DEFAULT 'agent' in
// the DB, but an older /host/my-kanban payload (deploy skew) may omit it, and
// treating undefined as agent preserves today's behaviour rather than
// over-suppressing every nudge during a rollout.
export function isHybridActionable(item: BoardItem): boolean {
  return (
    HYBRID_ACTIONABLE_STATUSES.has(item.status) &&
    item.source_type !== 'scheduled_task' &&
    item.assignee_type !== 'human'
  );
}

export function hasHybridActionableItems(items: BoardItem[]): boolean {
  return items.some(isHybridActionable);
}

/** What the hybrid kanban-inject block should do for a given board snapshot. */
export type HybridNudgeAction = 'nudge' | 'skip-stale' | 'close-suppressed' | 'drain';

/**
 * ENG-7958: decide what the manager's hybrid kanban-inject block does this tick.
 *
 * `stale` wins over everything. A stale snapshot came from the module-level
 * kanbanBoardCache after a failed /host/my-kanban fetch, so it is untrustworthy
 * in BOTH directions: its "actionable" cards may be long done (nudging off them
 * is the "empty board still nudges" bug this guards, ENG-7958) AND an apparent
 * drain may just be missing fresh cards (so we must not treat it as a real
 * drain either). Next tick re-fetches. Only on a FRESH snapshot do we act on
 * the board:
 *   - any allowed (todo/in_progress, non-poison) card  -> 'nudge'
 *   - else every actionable card is a suppressed poison card (ENG-6171)
 *                                                       -> 'close-suppressed'
 *   - else the board genuinely drained                 -> 'drain'
 *
 * Pure - no board fetch, no I/O - so the enqueue-gate decision is unit-testable
 * independent of the ~9000-line refreshAgent it lives in.
 */
export function resolveHybridNudgeAction(
  stale: boolean,
  allowedCount: number,
  suppressedCount: number,
): HybridNudgeAction {
  if (stale) return 'skip-stale';
  if (allowedCount > 0) return 'nudge';
  if (suppressedCount > 0) return 'close-suppressed';
  return 'drain';
}

// ENG-7704: cadence back-off for the hybrid kanban nudge.
//
// The nudge fires whenever the actionable board is non-empty, at the fast
// KANBAN_HYBRID_DEBOUNCE_MS cadence (~60s). Normally the durable direct-chat
// notice rail dedups (an `already_pending` notice is not re-enqueued), so an
// agent isn't re-nudged for an unchanged board until it consumes the notice.
// But when the enqueue keeps FAILING (transport/route error → send-keys
// fallback, which has no dedup), the same unchanged `todo`/`in_progress` board
// the agent already saw + stood down on is re-nudged every ~60-75s — a hot
// retry-loop that burns a turn (and churns a failed attribution run) each cycle.
//
// The fix: remember the actionable-board SIGNATURE we last nudged for. While it
// is unchanged, re-nudge only at the slow FULL cadence, not the fast one. Any
// change to the actionable set (a new card, or a status change) yields a new
// signature and nudges promptly, so fresh self-actionable work is never delayed
// past the fast cadence.

/**
 * Stable signature of the actionable ('allowed') cards: sorted `id:status`
 * pairs. Order-independent; a status change counts as new work. An empty list
 * (drained board) yields '' — callers handle drain separately and don't nudge.
 */
export function kanbanActionableSignature(allowed: BoardItem[]): string {
  return allowed
    .map((i) => `${i.id}:${i.status}`)
    .sort()
    .join('|');
}

/** Per-agent record of the last actionable signature we nudged for + when. */
export interface KanbanNudgeBackoffState {
  signature: string;
  nudgedAt: number;
  /**
   * ENG-7798: consecutive nudges delivered for THIS unchanged signature.
   * Every same-signature re-nudge means the previous nudge was a no-op (had
   * the agent moved a card, the signature would have changed), so this count
   * doubles as the consecutive-no-op counter that escalates the backoff.
   * Optional for compatibility with pre-ENG-7798 persisted state; treated
   * as 1 when absent.
   */
  nudgeCount?: number;
  /**
   * ENG-6876: consecutive DELIVERY failures (the enqueue onto the direct-chat
   * notice rail errored). Deliberately separate from `nudgeCount`: that counts
   * nudges that LANDED and were no-ops, this counts nudges that never landed.
   * Conflating them would let a transport outage look like an idle board.
   * Absent/0 means the last attempt succeeded.
   */
  failureCount?: number;
  /**
   * ENG-6876: when the last FAILED attempt was made. Separate from `nudgedAt`,
   * which must keep meaning "when we last successfully delivered" — the
   * unchanged-board ladder is anchored on it, and moving it on failure would
   * silently stretch that ladder for a reason it knows nothing about.
   */
  failedAt?: number;
}

/**
 * ENG-7798: the re-nudge interval for an unchanged board escalates with each
 * consecutive no-op nudge instead of repeating at the fixed full cadence
 * forever (a board with one blocked card burned a no-op turn every 5 minutes
 * indefinitely — ~288/day). Ladder: full, 3x, 9x, ... capped at maxCadenceMs
 * (default 5m -> 15m -> 45m -> 60m cap). Pure and exported for tests.
 */
export function nudgeIntervalForCount(
  nudgeCount: number,
  fullCadenceMs: number,
  maxCadenceMs: number,
): number {
  const exponent = Math.max(0, Math.min(nudgeCount - 1, 8)); // 3^8 bounds the pow
  return Math.min(fullCadenceMs * 3 ** exponent, Math.max(maxCadenceMs, fullCadenceMs));
}

/**
 * Should the hybrid nudge fire, given the current actionable signature and the
 * last-nudged state? Pure — `now` is injected for tests.
 *  - No prior state (never nudged this agent) → nudge.
 *  - Signature CHANGED since the last nudge (new/changed actionable card) →
 *    nudge (fresh work; the caller's fast debounce still guards double-fire).
 *  - Signature UNCHANGED → nudge only once the escalated cadence has elapsed
 *    (ENG-7798: full cadence for the first repeat, then 3x per consecutive
 *    no-op up to maxCadenceMs), so an idle "nothing new to self-action" board
 *    backs off harder the longer it stays blocked instead of re-firing at the
 *    full cadence forever.
 */
export function shouldNudgeUnchangedBoard(
  signature: string,
  state: KanbanNudgeBackoffState | undefined,
  now: number,
  fullCadenceMs: number,
  maxCadenceMs: number = fullCadenceMs,
): boolean {
  if (!state) return true;
  if (state.signature !== signature) return true;
  return now - state.nudgedAt >= nudgeIntervalForCount(state.nudgeCount ?? 1, fullCadenceMs, maxCadenceMs);
}

/**
 * ENG-6876: how long to wait before RETRYING after consecutive delivery
 * failures. Same 3x ladder as the unchanged-board cadence, but anchored on the
 * fast debounce rather than the full cadence: a transport error is not an idle
 * board, and the first retry should still be prompt.
 *
 * Defaults (60s debounce, 60m ceiling): 60s -> 3m -> 9m -> 27m -> 60m cap.
 */
export function failureRetryIntervalForCount(
  failureCount: number,
  baseMs: number,
  maxMs: number,
): number {
  const exponent = Math.max(0, Math.min(failureCount - 1, 8)); // 3^8 bounds the pow
  return Math.min(baseMs * 3 ** exponent, Math.max(maxMs, baseMs));
}

/** ENG-6876: consecutive delivery failures after which the breaker opens. */
export const KANBAN_NOTICE_BREAKER_THRESHOLD = 5;

export type KanbanNudgeGateDecision =
  /** No failures recorded (or the retry window has elapsed): attempt delivery. */
  | { attempt: true; breakerOpen: boolean }
  /** Inside the escalated retry window, or the breaker is open: skip silently. */
  | { attempt: false; breakerOpen: boolean };

/**
 * ENG-6876: should we ATTEMPT a notice enqueue at all, given prior consecutive
 * delivery failures? Pure — `now` is injected for tests. Applied BEFORE the
 * unchanged-board gate, because a transport failure is independent of what the
 * board looks like.
 *
 * THE LOAD-BEARING DECISION: a signature change does NOT reset this. It is
 * tempting — fresh work feels like it deserves a fresh attempt — but the whole
 * failure mode is that the rail is down, and on an active board the signature
 * changes constantly. Resetting on it would re-establish the every-debounce
 * loop this exists to damp, exactly on the busiest agents. Only a SUCCESSFUL
 * enqueue clears the failure state.
 *
 * The breaker (>= KANBAN_NOTICE_BREAKER_THRESHOLD consecutive failures) does
 * not stop retrying — a permanently-silent manager would be a worse failure
 * than a chatty one. It pins the interval at the ceiling and flags the state so
 * the caller raises ONE deduped alert instead of a log line per tick.
 */
export function shouldAttemptNoticeEnqueue(
  state: KanbanNudgeBackoffState | undefined,
  now: number,
  baseMs: number,
  maxMs: number,
  breakerThreshold: number = KANBAN_NOTICE_BREAKER_THRESHOLD,
): KanbanNudgeGateDecision {
  const failures = state?.failureCount ?? 0;
  if (failures <= 0) return { attempt: true, breakerOpen: false };
  const breakerOpen = failures >= breakerThreshold;
  // `failedAt` is written whenever failureCount is, so a state with failures
  // but no timestamp is corrupt/hand-edited. Both that and a FUTURE timestamp
  // (CodeRabbit, PR #4309 — a clock correction, or an edited state file) are
  // treated as DUE: `now - failedAt` would otherwise be negative and never
  // reach the interval, suppressing this agent's notices for an arbitrary time
  // with no log and no way back. Fail toward delivering the nudge.
  const failedAt = state?.failedAt;
  const due =
    failedAt === undefined ||
    failedAt > now ||
    now - failedAt >= failureRetryIntervalForCount(failures, baseMs, maxMs);
  return { attempt: due, breakerOpen };
}

// Slash commands go via tmux send-keys directly (and carry no marker);
// plain-text nudges (e.g. the kanban check) go through injectMessage with a
// run marker appended.
export function isSlashCommand(command: string): boolean {
  return command.trimStart().startsWith('/');
}

export const BOARD_INJECT_TEMPLATES = new Set(['morning-plan', 'task-update', 'hourly-status', 'end-of-day-summary']);

// ENG-4410: Check if the board has work the kanban-work loop will actually act
// on, used to skip the kanban-work claude -p spawn when there's nothing to do.
// ENG-5605: `backlog` is deliberately EXCLUDED. The kanban-work loop only works
// `todo`/`in_progress` cards (it promotes the top `todo`); a card parked in
// `backlog` is curatorial, not ready. Counting backlog here made an agent with
// backlog cards spawn claude -p every ~5 min only to return `<no-delivery/>` —
// no-op subprocess spawns re-paying the full prompt-cache prefix for zero work
// (observed on maven, agt-aws-1). This now matches HYBRID_ACTIONABLE_STATUSES.
export const ACTIONABLE_STATUSES = new Set(['todo', 'in_progress']);
export function hasActionableItems(items: BoardItem[]): boolean {
  return items.some((item) => ACTIONABLE_STATUSES.has(item.status));
}

export function parseStandupSummary(summary: string): { yesterday: string; today: string; blockers: string } {
  // Try to extract sections from the standup text
  const lines = summary.split('\n');
  let yesterday = '';
  let today = '';
  let blockers = '';
  let currentSection: 'yesterday' | 'todo' | 'blockers' | null = null;

  for (const line of lines) {
    const lower = line.toLowerCase();
    if (lower.includes('yesterday') || lower.includes('accomplished')) {
      currentSection = 'yesterday';
      continue;
    } else if (lower.includes('today') || lower.includes('todo') || lower.includes('working on')) {
      // `today` is checked AFTER the yesterday/accomplished branch above, so a
      // "Today:" header maps to the todo section while "yesterday" (which has no
      // `today` substring) still wins its own branch. CodeRabbit PR #2140.
      currentSection = 'todo';
      continue;
    } else if (lower.includes('blocker')) {
      currentSection = 'blockers';
      continue;
    }

    const trimmed = line.replace(/^[-*•]\s*/, '').trim();
    if (!trimmed) continue;

    switch (currentSection) {
      case 'yesterday': yesterday += (yesterday ? '\n' : '') + trimmed; break;
      case 'todo': today += (today ? '\n' : '') + trimmed; break;
      case 'blockers': blockers += (blockers ? '\n' : '') + trimmed; break;
    }
  }

  // Fallback: if parsing didn't find sections, use full summary
  if (!yesterday && !today && !blockers) {
    today = summary;
  }

  return { yesterday, today, blockers };
}

export function parsePlanItems(summary: string): Array<{
  title: string;
  description?: string;
  priority: number;
  estimated_minutes?: number;
  status: string;
}> {
  const items: Array<{
    title: string;
    description?: string;
    priority: number;
    estimated_minutes?: number;
    status: string;
  }> = [];

  const lines = summary.split('\n');
  let currentItem: (typeof items)[0] | null = null;

  for (const line of lines) {
    const trimmed = line.trim();

    // Match numbered/bulleted items: "1. [HIGH] Task title (~30min)" or "- [MEDIUM] Task (~2hr)"
    const itemMatch = trimmed.match(/^(?:\d+[\.\)]\s*|[-*•]\s*)\[?(HIGH|MEDIUM|LOW|MED)\]?\s*(.+)/i);

    if (itemMatch) {
      // Push previous item
      if (currentItem) items.push(currentItem);

      const priorityStr = itemMatch[1]!.toUpperCase();
      const rest = itemMatch[2]!;

      // Extract time estimate
      let estimatedMinutes: number | undefined;
      const timeMatch = rest.match(/\(~?(\d+)\s*(min(?:utes?)?|hr?(?:ours?)?|h)\)/i);
      if (timeMatch) {
        const val = parseInt(timeMatch[1]!, 10);
        const unit = timeMatch[2]!.toLowerCase();
        estimatedMinutes = unit.startsWith('h') ? val * 60 : val;
      }

      // Title is everything except the time estimate — sanitize LLM output
      const title = sanitizeKanbanString(
        rest.replace(/\(~?\d+\s*(?:min(?:utes?)?|hr?(?:ours?)?|h)\)/i, ''),
        MAX_KANBAN_TITLE_LENGTH,
      );
      if (!title) continue;

      const priorityMap: Record<string, number> = { HIGH: 1, MEDIUM: 2, MED: 2, LOW: 3 };
      const priority = priorityMap[priorityStr] ?? 2;
      if (![1, 2, 3].includes(priority)) continue;

      currentItem = {
        title,
        priority,
        estimated_minutes: estimatedMinutes,
        status: 'todo',
      };
    } else if (currentItem && trimmed && !trimmed.match(/^(?:PLAN|---)/i)) {
      // Non-numbered line after a plan item = description (length-limited)
      const descLine = sanitizeKanbanString(trimmed, MAX_KANBAN_NOTES_LENGTH);
      currentItem.description = currentItem.description
        ? sanitizeKanbanString(currentItem.description + '\n' + descLine, MAX_KANBAN_NOTES_LENGTH)
        : descLine;
    }
  }

  if (currentItem) items.push(currentItem);
  return items;
}

export const VALID_KANBAN_STATUSES = new Set(['backlog', 'todo', 'in_progress', 'done']);
const MAX_KANBAN_TITLE_LENGTH = 500;
const MAX_KANBAN_NOTES_LENGTH = 2000;

/** Sanitize a string from LLM output: trim + length limit */
export function sanitizeKanbanString(value: string, maxLen: number): string {
  if (!value) return '';
  return value
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '') // strip control chars (keep \n, \r, \t)
    .replace(/\{\{.*?\}\}/g, '')                          // strip template injection markers
    .replace(/^(System|Assistant|Human):\s*/gmi, '')      // strip prompt-role prefixes
    .replace(/#{2,}/g, '')                                // strip markdown heading markers
    .replace(/\s+/g, ' ')                                 // collapse whitespace
    .trim()
    .slice(0, maxLen);
}

/** Sanitize all external fields on a board item before caching / prompt injection */
export function sanitizeBoardItem<T extends { title: string; status: string; deliverable?: string }>(item: T): T {
  return {
    ...item,
    title: sanitizeKanbanString(item.title, 200),
    status: sanitizeKanbanString(item.status, 50),
    ...(item.deliverable ? { deliverable: sanitizeKanbanString(item.deliverable, 500) } : {}),
  };
}

// ---------------------------------------------------------------------------

export function parseKanbanUpdates(summary: string): Array<{
  title: string;
  status: string;
  notes?: string;
  result?: string;
}> {
  const updates: Array<{ title: string; status: string; notes?: string; result?: string }> = [];

  // Look for the "KANBAN UPDATE:" section. Case-insensitive so LLM output that
  // varies the casing ("Kanban Update:", "kanban update:") still parses — the
  // section start is the matched index + the matched token's length. CodeRabbit
  // PR #2140.
  const markerMatch = /kanban update:/i.exec(summary);
  if (!markerMatch) return updates;

  const kanbanSection = summary.slice(markerMatch.index + markerMatch[0].length);
  const lines = kanbanSection.split('\n');

  for (const line of lines) {
    const trimmed = line.trim();
    // Match: - "item title": status (optional notes/result)
    // Accept legacy 'today' as a synonym for 'todo' so prompts that haven't
    // been re-rendered post-rename still parse cleanly. Map to 'todo' before
    // validating against VALID_KANBAN_STATUSES (which only contains 'todo').
    const match = trimmed.match(/^[-*•]\s*"([^"]+)":\s*(backlog|todo|today|in_progress|done)(?:\s*\((.+)\))?/i);
    if (match) {
      const rawStatus = match[2]!.toLowerCase();
      const status = rawStatus === 'today' ? 'todo' : rawStatus;
      // Validate status is in the allowed set
      if (!VALID_KANBAN_STATUSES.has(status)) continue;

      const title = sanitizeKanbanString(match[1]!, MAX_KANBAN_TITLE_LENGTH);
      if (!title) continue;

      const parenthetical = match[3] ?? undefined;

      // Extract result from parenthetical when status is done
      // Pattern: "result: <value>" or just the whole parenthetical as notes
      let notes: string | undefined;
      let result: string | undefined;

      if (parenthetical && status === 'done') {
        const resultMatch = parenthetical.match(/^result:\s*(.+)/i);
        if (resultMatch) {
          result = sanitizeKanbanString(resultMatch[1]!, MAX_KANBAN_NOTES_LENGTH);
        } else {
          notes = sanitizeKanbanString(parenthetical, MAX_KANBAN_NOTES_LENGTH);
        }
      } else if (parenthetical) {
        notes = sanitizeKanbanString(parenthetical, MAX_KANBAN_NOTES_LENGTH);
      }

      updates.push({
        title,
        status,
        notes,
        result,
      });
    }
  }

  return updates;
}

/**
 * ENG-5769: render the kanban board for prompt injection.
 *
 * Why the source coords matter: after ENG-5382, `kanban_add` auto-captures
 * the originating Slack/Telegram thread on the card (DB columns
 * `source_integration` / `source_external_id`, public-shape names
 * `source_channel` / `source_thread_id`). When a kanban-work nudge picks
 * the card back up post-restart, the agent needs those coordinates to
 * reply in the thread that asked the question — not the default channel.
 *
 * Prior to ENG-5769 this function rendered only title/priority/estimate/
 * deliverable, so the agent silently lost thread continuity on resume
 * unless `AGT_ACTIVE_TASKS_INJECT=1` (the default-off CLAUDE.md path) was
 * on. Now todo + in_progress rows include an inline `source:` sub-line
 * when coordinates are present, and the trailing guidance tells the
 * agent to reply in that thread on completion.
 *
 * Exported so the source-coord rendering can be unit-tested without
 * standing up the full manager-worker poll loop.
 */
export function formatBoardForPrompt(
  items: Array<{
    title: string;
    status: string;
    priority: number;
    estimated_minutes?: number;
    deliverable?: string;
    source_integration?: string | null;
    source_external_id?: string | null;
    source_url?: string | null;
  }>,
  template: 'morning-plan' | 'follow-up',
): string {
  if (items.length === 0) return '';

  const priorityLabel = (p: number) => p === 1 ? 'HIGH' : p === 3 ? 'LOW' : 'MED';
  const timeLabel = (m?: number) => m ? ` (~${m >= 60 ? `${Math.round(m / 60)}hr` : `${m}min`})` : '';
  const deliverableLine = (d?: string) => d ? `\n     Deliverable: ${d}` : '';
  // Source rendering mirrors `buildActiveTasksSection` (claudecode/identity.ts)
  // so both surfaces stay consistent (AC #3). A row with channel+thread reads
  // as `slack thread C123:1700000000.1`; a row with only a URL falls back to
  // the URL alone (rare — older rows from before ENG-5382's universal capture).
  const sourceLine = (
    channel?: string | null,
    thread?: string | null,
    url?: string | null,
  ): string => {
    const parts: string[] = [];
    if (channel && thread) parts.push(`${channel} thread ${thread}`);
    if (url) parts.push(url);
    return parts.length > 0 ? `\n     source: ${parts.join(' • ')}` : '';
  };

  const grouped: Record<string, typeof items> = {};
  for (const item of items) {
    const key = item.status;
    if (!grouped[key]) grouped[key] = [];
    grouped[key]!.push(item);
  }

  // Whether any actionable card carries a source thread — drives the
  // "reply in the originating thread" guidance below. No false positives
  // for boards that are all internal tasks.
  const hasSourceThread = items.some(
    (i) =>
      (i.status === 'todo' || i.status === 'in_progress') &&
      !!i.source_integration &&
      !!i.source_external_id,
  );

  const lines: string[] = [];

  if (template === 'morning-plan') {
    lines.push('=== CURRENT BOARD ===');

    for (const [status, label] of [['backlog', 'BACKLOG (carry-over)'], ['todo', 'TO DO'], ['in_progress', 'IN PROGRESS']] as const) {
      const statusItems = grouped[status];
      if (statusItems && statusItems.length > 0) {
        lines.push(`${label}:`);
        statusItems.forEach((item, i) => {
          lines.push(`  ${i + 1}. [${priorityLabel(item.priority)}] ${item.title}${timeLabel(item.estimated_minutes)}${deliverableLine(item.deliverable)}${sourceLine(item.source_integration, item.source_external_id, item.source_url)}`);
        });
      }
    }

    lines.push('=====================');
    lines.push('');
    lines.push('Create today\'s plan. You may:');
    lines.push('- Move backlog items to "todo"');
    lines.push('- Add new items you\'ve identified');
    lines.push('- Reprioritise existing items');
    lines.push('');
  } else {
    lines.push('=== YOUR KANBAN BOARD ===');

    for (const [status, label] of [['todo', 'TO DO'], ['in_progress', 'IN PROGRESS'], ['backlog', 'BACKLOG']] as const) {
      const statusItems = grouped[status];
      if (statusItems && statusItems.length > 0) {
        lines.push(`${label}:`);
        statusItems.forEach((item, i) => {
          lines.push(`  ${i + 1}. [${priorityLabel(item.priority)}] ${item.title}${timeLabel(item.estimated_minutes)}${deliverableLine(item.deliverable)}${sourceLine(item.source_integration, item.source_external_id, item.source_url)}`);
        });
      }
    }

    const doneItems = grouped['done'];
    if (doneItems && doneItems.length > 0) {
      lines.push('DONE TODAY:');
      doneItems.forEach((item, i) => {
        lines.push(`  ${i + 1}. ${item.title}`);
      });
    }

    lines.push('=========================');
    lines.push('');
    lines.push('IMPORTANT: Use kanban MCP tools to update the board IN REAL TIME:');
    lines.push('1. FIRST call kanban_move to move your chosen item to in_progress BEFORE starting work');
    lines.push('2. Do the work');
    lines.push('3. Call kanban_done with a result summary when finished');
    lines.push('4. If blocked, call kanban_update with notes, then pick the next item');
    lines.push('');
    if (hasSourceThread) {
      // ENG-5769: thread-continuity guidance is conditional so boards
      // without channel-sourced cards don't carry instructions for a
      // case that can't apply.
      lines.push('THREAD CONTINUITY: If a card shows a `source:` line, the request originated');
      lines.push('in that Slack/Telegram thread. When you deliver the result, reply in that');
      lines.push('thread (not the default channel) so the user sees the answer where they');
      lines.push('asked. The card\'s `source_channel` + `source_thread_id` are the thread');
      lines.push('coordinates to use.');
      lines.push('');
    }
    lines.push('SELF-MANAGEMENT: When you receive a request from a channel (Slack, Telegram)');
    lines.push('that takes more than a quick response, create a task first with kanban_add,');
    lines.push('move to in_progress, do the work, then kanban_done with a result summary.');
    lines.push('');
    lines.push('If MCP tools are unavailable, include a KANBAN UPDATE section in your output:');
    lines.push('KANBAN UPDATE:');
    lines.push('- "item title": new_status (optional notes)');
    lines.push('- "item title": done (result: <what you produced>)');
    lines.push('Statuses: backlog, today, in_progress, done');
    lines.push('');
  }

  return lines.join('\n');
}
